import { Box, Divider, ListItemIcon, MenuItem, Nullable, Popover, Stack, Typography, styled as UIToolkitStyled } from '@ethings/ui-toolkit';
import { styled } from '@mui/material';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth';
import { Portal } from '../../types';
import { portalDefinitions, useHasAuthorization } from '../../util';
import { sideMenuTransitionStyles } from './side-menu.utils';

interface SideMenuPortalSelectProps {
  isOpen: boolean;
  portal: Portal;
}

const StyledPopover = UIToolkitStyled(Popover)({
  zIndex: 1400,
});

export const SideMenuPortalSelect: FC<SideMenuPortalSelectProps> = ({ isOpen, portal }) => {
  const { t } = useTranslation(['common']);
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null);
  const { state } = useAuth();
  const { hasAuthorization } = useHasAuthorization(state);

  const openPopup = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const onClose = (): void => {
    setAnchorEl(null);
  };

  const isPopupOpen = Boolean(anchorEl);

  const currentPortal = portalDefinitions[portal];
  const Icon = currentPortal.icon;
  const label = currentPortal.label;

  const portalsToDisplay = Object.entries(portalDefinitions).filter(([portalName, { roleFunction }]) => portal !== portalName && hasAuthorization(roleFunction)).map(([portal, p]) => ({ ...p, testId: `portal-navigation-${portal}` }));

  if (portalsToDisplay.length === 0) {
    return null;
  }

  return (
    <>
      <StyledPopover
        anchorEl={anchorEl}
        open={isPopupOpen}
        onClose={onClose}
        onClick={(e) => { e.stopPropagation(); }}
        slotProps={{ paper: { sx: { minWidth: 265 } } }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Stack spacing={1} py={1}>
          <Box px={2}>
            <Typography variant="caption">{t('common:component.navigation.switch-to.labels')}</Typography>
          </Box>
          <div>
            {portalsToDisplay.map((portal, i) => {
              const Icon = portal.icon;

              return (
                <MenuItem
                  key={i}
                  component="a"
                  // @ts-expect-error component prop is not handled correctly
                  href={portal.path}
                  data-testid={portal.testId}
                >
                  <StyledListItemIcon>
                    <Icon />
                  </StyledListItemIcon>
                  <Typography>{t(portal.label)}</Typography>
                </MenuItem>
              );
            })}
          </div>
        </Stack>
      </StyledPopover>
      <Divider />
      <PortalSelectButton data-testid="portal-select-button" onClick={openPopup} isOpen={isOpen}>
        <IconWrap direction="row" alignItems="center" spacing={1}>
          <Icon fontSize="small" />
          <Label variant="body2" component="span" style={{ opacity: isOpen ? 1 : 0 }}>{t(label)}</Label>
        </IconWrap>
        <Icon icon="ChevronDown" style={{ opacity: isOpen ? 1 : 0 }} />
      </PortalSelectButton>
    </>
  );
};

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  color: `${theme.palette.primary.main} !important`,
}));

const PortalSelectButton = styled('button')<{ isOpen: boolean }>(({ theme, isOpen }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  cursor: 'pointer',
  gap: theme.spacing(1),
  paddingTop: theme.spacing(2),
  paddingRight: theme.spacing(isOpen ? 2 : 1),
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(isOpen ? 2 : 1),
  ...sideMenuTransitionStyles,
}));

const IconWrap = styled(Stack)(({ theme }) => ({
  '& svg': {
    color: theme.sidebar.icons.color,
  },
}));

const Label = styled(Typography)(() => ({
  whiteSpace: 'nowrap',
  ...sideMenuTransitionStyles,
}));
